import { useState } from 'react';
import {
	DocumentClass,
	DocumentClassSchema,
	update,
} from '@/api/doc/document-class';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateDocumentClass({
	documentClass,
}: {
	documentClass: DocumentClass;
}) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedDocument: DocumentClass) => update(updatedDocument),
		onSuccess: () => {
			toast.success(`Success! The document class has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({ queryKey: ['document-classes'] });
		},
		onError: () => {
			toast.error(`Error! The document class could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<DocumentClass>({
		resolver: zodResolver(DocumentClassSchema),
		defaultValues: documentClass,
		mode: 'all',
	});

	const deletedDescription: string = !documentClass.deletedOn
		? 'Toggle on to delete this document class.'
		: 'Toggle off to not delete this document class.';

	const deletedLabel: string = !documentClass.deletedOn ? 'Delete?' : 'Deleted';

	const onSubmit: SubmitHandler<DocumentClass> = (values: DocumentClass) =>
		mutate(values);

	form.watch();

	console.log(form.formState.errors);
	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update document class</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent className="overflow-scroll">
					<SheetHeader>
						<SheetTitle>Update Document Class</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Document Class Name</FormLabel>
										<FormControl>
											<Input {...field} placeholder="Document class name" />
										</FormControl>
										<FormDescription>Name the document class</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="deletedOn"
								render={({ field }) => (
									<FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
										<div className="space-y-0.5">
											<FormLabel>{deletedLabel}</FormLabel>
											<FormDescription>{deletedDescription}</FormDescription>
										</div>
										<FormControl>
											<Switch
												checked={!!field.value}
												onCheckedChange={() =>
													form.setValue('deletedOn', field.value ? null : new Date())
												}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
